import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { ChatMessage as ChatMessageType, SuggestedPrompt } from '@/types/chatbot.types';
import ChatMessage from './ChatMessage';
import AISparkleIcon from '@/comp/common/icons/AISparkleIcon';
import { useChatScroll } from '@/hooks/useChatScroll';
import * as S from './ChatMessages.styles';
import { theme } from 'antd';

const { useToken } = theme;

interface ChatMessagesProps {
  messages: ChatMessageType[];
  suggestedPrompts?: SuggestedPrompt[];
  onPromptClick?: (prompt: string) => void;
  onPublishClick?: (insightId: string) => void;
  renderInput?: () => React.ReactNode;
  disabled?: boolean;
  onResendMessage?: () => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = observer(
  ({
    messages,
    suggestedPrompts = [],
    onPromptClick,
    onPublishClick,
    renderInput,
    disabled = false,
    onResendMessage,
  }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const { messagesEndRef, messagesContainerRef } = useChatScroll(messages);

    if (messages.length === 0) {
      return (
        <S.MessagesContainer>
          <S.MessagesContent>
            <S.EmptyState>
              <div className="sparkle-icon">
                <AISparkleIcon width={29} height={29} color={token.colorPrimary} />
              </div>
              <h3 className="title">{t('GetStartedTitle')}</h3>
              <p className="description">
                {t('SelectInsightDescription')} <br />
                {t('ExploreStationPerformance')}
              </p>

              {renderInput && <div className="input-wrapper">{renderInput()}</div>}

              {suggestedPrompts.length > 0 && (
                <>
                  <p className="helper-text">{t('SalesAssistantHelperText')}</p>
                  <S.SuggestedPromptsContainer className="no-margin-top">
                    {suggestedPrompts.map(prompt => (
                      <S.SuggestedPromptButton
                        key={prompt.id}
                        onClick={() => !disabled && onPromptClick?.(prompt.prompt)}
                        disabled={disabled}
                      >
                        {prompt.badge && <span className="badge">{prompt.badge}</span>}
                        {prompt.label}
                      </S.SuggestedPromptButton>
                    ))}
                  </S.SuggestedPromptsContainer>
                </>
              )}
            </S.EmptyState>
          </S.MessagesContent>
        </S.MessagesContainer>
      );
    }

    return (
      <S.MessagesContainer ref={messagesContainerRef}>
        <S.MessagesContent>
          {messages.map(message => (
            <ChatMessage
              key={message.id}
              message={message}
              onPublishClick={onPublishClick}
              onResendMessage={onResendMessage}
            />
          ))}
          <div ref={messagesEndRef} />
        </S.MessagesContent>
      </S.MessagesContainer>
    );
  },
);

ChatMessages.displayName = 'ChatMessages';

export default ChatMessages;
